import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Users, Shield, FileCheck, AlertTriangle, Plus,
  Trash2, Edit, Home, CheckCircle, XCircle, Clock,
  Flame, Lightbulb, DoorOpen, ChefHat, Bath, Ruler
} from 'lucide-react';

const API_URL = getApiBaseUrl();
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

export function HMOManagement({ propertyId, jurisdiction }) {
  const [loading, setLoading] = useState(true);
  const [compliance, setCompliance] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [communalAreas, setCommunalAreas] = useState([]);
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [newRoom, setNewRoom] = useState({
    name: '',
    floor: 0,
    size_sqm: 0,
    ensuite: false,
    furnished: true,
    rent_amount: 0,
    features: []
  });

  useEffect(() => {
    if (propertyId) {
      loadData();
    }
  }, [propertyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [complianceRes, roomsRes, areasRes] = await Promise.all([
        fetch(`${API_URL}/api/property-types/properties/${propertyId}/hmo-compliance`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        }),
        fetch(`${API_URL}/api/property-types/properties/${propertyId}/rooms`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        }),
        fetch(`${API_URL}/api/property-types/properties/${propertyId}/communal-areas`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        })
      ]);

      if (complianceRes.ok) {
        setCompliance(await complianceRes.json());
      }
      if (roomsRes.ok) {
        const data = await roomsRes.json();
        setRooms(data.rooms || []);
      }
      if (areasRes.ok) {
        const data = await areasRes.json();
        setCommunalAreas(data.areas || []);
      }
    } catch (error) {
      console.error('Failed to load HMO data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addRoom = async () => {
    try {
      const response = await fetch(`${API_URL}/api/property-types/properties/${propertyId}/rooms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newRoom)
      });

      if (response.ok) {
        toast.success('Room added');
        setShowAddRoom(false);
        setNewRoom({ name: '', floor: 0, size_sqm: 0, ensuite: false, furnished: true, rent_amount: 0, features: [] });
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add room');
      }
    } catch (error) {
      toast.error('Failed to add room');
    }
  };

  const deleteRoom = async (roomId) => {
    if (!confirm('Delete this room?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/property-types/rooms/${roomId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (response.ok) {
        toast.success('Room deleted');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete room');
      }
    } catch (error) {
      toast.error('Failed to delete room');
    }
  };

  const updateRoomStatus = async (roomId, status) => {
    try {
      const response = await fetch(`${API_URL}/api/property-types/rooms/${roomId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success('Room status updated');
        loadData();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* HMO Compliance Status */}
      <Card className="p-4 sm:p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          HMO Compliance
        </h3>
        
        {compliance && (
          <div className="space-y-4">
            {/* License Status */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div>
                <p className="font-medium">HMO License</p>
                <p className="text-sm text-slate-500">
                  {compliance.hmo_license_required ? 'Required for this property' : 'May not be required'}
                </p>
              </div>
              <StatusBadge status={compliance.license_status} />
            </div>

            {/* Compliance Checklist */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ComplianceItem 
                icon={Flame} 
                label="Fire Risk Assessment" 
                status={compliance.fire_risk_assessment} 
              />
              <ComplianceItem 
                icon={DoorOpen} 
                label="Fire Doors" 
                status={compliance.fire_doors_installed} 
              />
              <ComplianceItem 
                icon={Lightbulb} 
                label="Emergency Lighting" 
                status={compliance.emergency_lighting} 
              />
              <ComplianceItem 
                icon={ChefHat} 
                label="Kitchen Facilities" 
                status={compliance.kitchen_facilities_adequate} 
              />
              <ComplianceItem 
                icon={Bath} 
                label="Bathroom Ratio (1:5)" 
                status={compliance.bathroom_ratio_compliant} 
              />
              <ComplianceItem 
                icon={Ruler} 
                label="Room Sizes" 
                status={compliance.room_sizes_compliant} 
              />
            </div>

            {/* Issues */}
            {compliance.compliance_issues?.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Compliance Issues
                </p>
                <ul className="text-sm text-amber-700 space-y-1">
                  {compliance.compliance_issues.map((issue, i) => (
                    <li key={i}>• {issue}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Room Inventory */}
      <Card className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Home className="w-5 h-5 text-primary" />
            Room Inventory ({rooms.length})
          </h3>
          <Button size="sm" onClick={() => setShowAddRoom(true)}>
            <Plus className="w-4 h-4 mr-1" /> Add Room
          </Button>
        </div>

        {/* Add Room Form */}
        {showAddRoom && (
          <Card className="p-4 mb-4 border-primary">
            <h4 className="font-medium mb-3">New Room</h4>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Room name (e.g., Room 1)"
                value={newRoom.name}
                onChange={(e) => setNewRoom({...newRoom, name: e.target.value})}
                className="col-span-2 px-3 py-2 border rounded-lg"
              />
              <input
                type="number"
                placeholder="Floor"
                value={newRoom.floor}
                onChange={(e) => setNewRoom({...newRoom, floor: parseInt(e.target.value) || 0})}
                className="px-3 py-2 border rounded-lg"
              />
              <input
                type="number"
                placeholder="Size (sqm)"
                value={newRoom.size_sqm}
                onChange={(e) => setNewRoom({...newRoom, size_sqm: parseFloat(e.target.value) || 0})}
                className="px-3 py-2 border rounded-lg"
              />
              <input
                type="number"
                placeholder="Rent (£/month)"
                value={newRoom.rent_amount}
                onChange={(e) => setNewRoom({...newRoom, rent_amount: parseFloat(e.target.value) || 0})}
                className="px-3 py-2 border rounded-lg"
              />
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newRoom.ensuite}
                  onChange={(e) => setNewRoom({...newRoom, ensuite: e.target.checked})}
                  className="w-4 h-4"
                />
                Ensuite
              </label>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={addRoom}>Save Room</Button>
              <Button size="sm" variant="outline" onClick={() => setShowAddRoom(false)}>Cancel</Button>
            </div>
          </Card>
        )}

        {/* Room List */}
        {rooms.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No rooms added yet</p>
        ) : (
          <div className="space-y-3">
            {rooms.map((room) => (
              <div key={room.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{room.name}</span>
                    <RoomStatusBadge status={room.status} />
                    {room.ensuite && (
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Ensuite</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">
                    Floor {room.floor || 0} • {room.size_sqm || '?'} sqm • £{room.rent_amount}/mo
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={room.status}
                    onChange={(e) => updateRoomStatus(room.id, e.target.value)}
                    className="text-sm border rounded px-2 py-1"
                  >
                    <option value="available">Available</option>
                    <option value="occupied">Occupied</option>
                    <option value="reserved">Reserved</option>
                    <option value="offline">Offline</option>
                  </select>
                  <Button size="sm" variant="ghost" onClick={() => deleteRoom(room.id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Occupancy Summary */}
      <Card className="p-4 sm:p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          Occupancy Summary
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-2xl font-bold">{rooms.length}</p>
            <p className="text-sm text-slate-500">Total Rooms</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">
              {rooms.filter(r => r.status === 'occupied').length}
            </p>
            <p className="text-sm text-slate-500">Occupied</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">
              {rooms.filter(r => r.status === 'available').length}
            </p>
            <p className="text-sm text-slate-500">Available</p>
          </div>
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-2xl font-bold">
              {rooms.length > 0 
                ? Math.round((rooms.filter(r => r.status === 'occupied').length / rooms.length) * 100)
                : 0}%
            </p>
            <p className="text-sm text-slate-500">Occupancy</p>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Helper Components
function StatusBadge({ status }) {
  const styles = {
    valid: 'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    expired: 'bg-red-100 text-red-700',
    not_required: 'bg-slate-100 text-slate-600'
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${styles[status] || styles.pending}`}>
      {status?.replace('_', ' ') || 'Pending'}
    </span>
  );
}

function RoomStatusBadge({ status }) {
  const styles = {
    available: 'bg-green-100 text-green-700',
    occupied: 'bg-blue-100 text-blue-700',
    reserved: 'bg-amber-100 text-amber-700',
    offline: 'bg-slate-100 text-slate-600'
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${styles[status] || styles.available}`}>
      {status}
    </span>
  );
}

function ComplianceItem({ icon: Icon, label, status }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${status ? 'bg-green-50' : 'bg-slate-50'}`}>
      <div className={`p-2 rounded-lg ${status ? 'bg-green-100' : 'bg-slate-200'}`}>
        <Icon className={`w-4 h-4 ${status ? 'text-green-600' : 'text-slate-500'}`} />
      </div>
      <span className="text-sm font-medium">{label}</span>
      {status ? (
        <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
      ) : (
        <XCircle className="w-4 h-4 text-slate-400 ml-auto" />
      )}
    </div>
  );
}

export default HMOManagement;
